import { useEffect, useRef, useState, useCallback } from "react";
import "@/App.css";
import emailjs from '@emailjs/browser';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Toaster, toast } from 'sonner';
import { 
  Phone, Mail, MapPin, Clock, CheckCircle, ChevronDown, ChevronUp,
  Ship, Plane, Truck, FileText, Globe, Building2, Package, 
  Briefcase, Shield, Award, Users, TrendingUp, Star,
  Factory, Pill, Car, Cpu, FlaskConical, Shirt, ShoppingBag,
  Menu, X, ArrowRight, Send, MessageSquare
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";

gsap.registerPlugin(ScrollTrigger);

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ap-enterprises-build/artifacts/mq2byl1y_APE%20LOGO.png";
const HERO_BG = "https://customer-assets.emergentagent.com/job_ap-enterprises-build/artifacts/bqb8ag91_Perfect.jpeg";

const EMAILJS_CONFIG = {
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID,
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID
};

const WHATSAPP_LINK = "https://wa.me/918577000069?text=Hi%2C%20I%20need%20help%20with%20customs%20clearance";
const PHONE_NUMBER = "8577000069";
const EMAIL = "apenterprisesindia.cc@gmail.com";

const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/apecustomsclearing?igsh=MW1zMnNlYnU0N2tzZg%3D%3D&utm_source=qr",
  facebook: "https://www.facebook.com/profile.php?id=61579497509973",
  linkedin: "https://www.linkedin.com/in/a-p-enterprises-aa14603b8/",
  twitter: "https://x.com/apcustoms"
};

// Navbar Component
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'About Us', href: '#about' },
    { name: 'Industries', href: '#industries' },
    { name: 'Locations', href: '#locations' },
    { name: 'Blog', href: '#blog' },
    { name: 'FAQ', href: '#faq' },
  ];

  return (
    <nav data-testid="navbar" className={`navbar fixed top-0 left-0 right-0 z-50 ${scrolled ? 'scrolled' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo and Company Name */}
          <a href="#home" className="flex items-center gap-3" data-testid="logo-link">
            <div>
              <div className="font-bold text-slate-900 text-lg leading-tight">A.P Enterprises</div>
              <div className="text-xs text-slate-500 font-medium">Simplifying Customs Clearing</div>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </a>
            ))}
            <a
              href="#contact"
              className="btn-primary"
              data-testid="nav-contact-btn"
            >
              Contact Us
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`} data-testid="mobile-menu">
        <div className="flex flex-col gap-6">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className="text-lg font-medium text-slate-900"
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.name}
            </a>
          ))}
          <a
            href="#contact"
            className="btn-primary w-full text-center"
            onClick={() => setMobileMenuOpen(false)}
          >
            Contact Us
          </a>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section 
      id="home" 
      className="relative min-h-[60vh] md:min-h-[80vh] flex items-end pb-16 md:pb-24 pt-20"
      data-testid="hero-section"
    >
      {/* Hero Background Image - Full Width Banner */}
      <div 
        className="absolute inset-0 bg-slate-100"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      ></div>
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
        {/* CTA Buttons */}
        <div className="flex flex-wrap justify-end gap-4 fade-up mb-8" data-testid="hero-cta-buttons">
          <a href={`tel:+91${PHONE_NUMBER}`} className="btn-primary" data-testid="hero-call-btn">
            <Phone className="w-4 h-4" />
            Call Now
          </a>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-whatsapp" data-testid="hero-whatsapp-btn">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp Chat
          </a>
        </div>
      </div>
    </section>
  );
};

// Trust Bar Section
const TrustBar = () => {
  const indicators = [
    { icon: Award, text: "20+ Years Experience", color: "text-blue-600" },
    { icon: Shield, text: "CHA Licensed", color: "text-green-600" },
    { icon: TrendingUp, text: "Fast Clearance", color: "text-orange-500" },
    { icon: CheckCircle, text: "Government Compliance", color: "text-emerald-600" },
    { icon: Users, text: "Dedicated Client Support", color: "text-purple-600" },
  ];

  return (
    <section className="py-12 bg-slate-50 border-y border-slate-100" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {indicators.map((item, index) => (
            <div key={index} className="flex flex-col items-center text-center gap-3 fade-up" data-testid={`trust-item-${index}`}>
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm border border-slate-100">
                <item.icon className={`w-6 h-6 ${item.color}`} />
              </div>
              <span className="text-sm font-medium text-slate-700">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => {
  const services = [
    { icon: Ship, title: "Import/Export Customs Clearance", description: "Complete customs clearance services for all your import and export shipments with full compliance." },
    { icon: FileText, title: "Import/Export Documentation", description: "Expert handling of all documentation requirements for seamless international trade." },
    { icon: Plane, title: "Airport Cargo Clearance", description: "Swift clearance services at all major airports across India with priority handling." },
    { icon: Ship, title: "Sea Port Clearance", description: "Comprehensive clearance services at all major seaports ensuring timely delivery." },
    { icon: Building2, title: "ICD / CFS Clearance", description: "Efficient clearance at Inland Container Depots and Container Freight Stations." },
    { icon: FileText, title: "Documentation & Consulting", description: "Professional consulting and documentation services for complex trade scenarios." },
    { icon: Globe, title: "IEC, DGFT & GST Assistance", description: "Complete assistance with IEC registration, DGFT compliance, and GST procedures." },
    { icon: Truck, title: "Freight Forwarding", description: "End-to-end freight forwarding solutions for air, sea, and land transportation." },
    { icon: Package, title: "Logistics PAN India", description: "Nationwide logistics network ensuring delivery to any location across India." },
  ];

  return (
    <section id="services" className="py-24 md:py-32 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16 fade-up">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
            Services
          </h2>
          <p className="mt-4 text-base text-slate-500 max-w-2xl mx-auto">
            Comprehensive Customs Solutions
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div key={index} className="service-card fade-up" data-testid={`service-card-${index}`}>
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-6">
                <service.icon className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{service.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{service.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-12 fade-up">
          <a href="#contact" className="btn-primary" data-testid="services-cta">
            Get Started <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Process Section
const Process = () => {
  const steps = [
    { icon: MessageSquare, title: "Consult", description: "Discuss your requirements" },
    { icon: FileText, title: "Documentation", description: "Prepare all paperwork" },
    { icon: Send, title: "Customs Filing", description: "Submit to authorities" },
    { icon: CheckCircle, title: "Clearance", description: "Get approval" },
    { icon: Truck, title: "Delivery", description: "Receive your goods" },
  ];

  return (
    <section className="py-24 md:py-32 bg-slate-50" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16 fade-up">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
            Process
          </h2>
          <p className="mt-4 text-base text-slate-500 max-w-2xl mx-auto">
            Simple 5-Step Clearance
          </p>
        </div>

        <div className="relative">
          {/* Desktop Process Flow */}
          <div className="hidden md:flex justify-between items-start relative">
            {/* Connecting Line */}
            <div className="absolute top-12 left-[10%] right-[10%] h-0.5 bg-slate-200">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-slate-200 animate-pulse"></div>
            </div>
            
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col items-center text-center w-1/5 relative z-10 fade-up" data-testid={`process-step-${index}`}>
                <div className="w-24 h-24 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center mb-4 shadow-sm">
                  <step.icon className="w-10 h-10 text-slate-700" />
                </div>
                <span className="text-xs font-semibold text-blue-600 mb-1">Step {index + 1}</span>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">{step.title}</h3>
                <p className="text-sm text-slate-500">{step.description}</p>
              </div>
            ))}
          </div>

          {/* Mobile Process Flow */}
          <div className="md:hidden space-y-6">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start gap-4 fade-up" data-testid={`process-step-mobile-${index}`}>
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center shadow-sm">
                  <step.icon className="w-8 h-8 text-slate-700" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-blue-600">Step {index + 1}</span>
                  <h3 className="text-lg font-semibold text-slate-900">{step.title}</h3>
                  <p className="text-sm text-slate-500">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const About = () => {
  return (
    <section id="about" className="py-24 md:py-32 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="fade-up">
            <span className="section-subtitle">About Us</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-4">
              About Us
            </h2>
            <p className="text-base text-slate-500 mb-6">
              Two Decades of Trusted Customs Clearing Excellence
            </p>
            <p className="text-slate-600 mb-6 leading-relaxed">
              A.P Enterprises is a CHA licensed customs clearing agent with over 20 years of experience providing fast, compliant import and export customs clearance solutions across India. We specialize in simplifying complex customs procedures for businesses of all sizes.
            </p>
            
            <div className="bg-slate-50 rounded-2xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Our Founder</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Founded by <strong>Mr. Suranjan Kumar Singh</strong>, A.P Enterprises is built on over 25 years of hands-on experience in the customs clearing industry. He began his journey with a job, learning the trade from the ground up before starting his own business in 2004. Known for his honest approach, reliability, and strong client relationships, he has earned the trust of clients who have been working with him for more than two decades.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-xl">
                <h4 className="font-semibold text-slate-900 mb-2">Our Mission</h4>
                <p className="text-sm text-slate-600">To simplify customs clearance by delivering fast, reliable, and fully compliant solutions.</p>
              </div>
              <div className="p-4 bg-green-50 rounded-xl">
                <h4 className="font-semibold text-slate-900 mb-2">Our Vision</h4>
                <p className="text-sm text-slate-600">To become a trusted leader in customs clearance services across India.</p>
              </div>
            </div>
          </div>

          <div className="fade-up">
            <Timeline />
          </div>
        </div>
      </div>
    </section>
  );
};

// Timeline Component
const Timeline = () => {
  const milestones = [
    { year: "2004", title: "Company Founded", description: "A.P. Enterprises was founded with the mission of helping businesses navigate customs procedures efficiently." },
    { year: "2011", title: "CHA License", description: "Received official CHA License, enabling authorized customs clearance services across major ports and cargo terminals." },
    { year: "2015-2020", title: "Expansion", description: "Expanded operations across industries including manufacturing, electronics, pharma, and automotive." },
    { year: "2022", title: "10,000+ Shipments", description: "Successfully cleared 10,000+ import and export shipments across India." },
    { year: "Today", title: "Trusted Partner", description: "A trusted customs clearance partner for businesses operating through airports, seaports, ICDs, and CFS locations." },
  ];

  return (
    <div className="relative pl-8" data-testid="timeline">
      <div className="absolute left-[5px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-blue-500 to-slate-200"></div>
      
      {milestones.map((item, index) => (
        <div key={index} className="relative mb-8 last:mb-0 fade-up" data-testid={`timeline-item-${index}`}>
          <div className="absolute left-[-27px] top-1 w-4 h-4 rounded-full bg-blue-500 border-4 border-white shadow-sm"></div>
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm ml-4">
            <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full mb-2">{item.year}</span>
            <h4 className="font-semibold text-slate-900 mb-1">{item.title}</h4>
            <p className="text-sm text-slate-600">{item.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Why Choose Us Section with Counters
const WhyChooseUs = () => {
  const stats = [
    { value: 20, suffix: "+", label: "Years Experience" },
    { value: 500, suffix: "+", label: "Clients Served" },
    { value: 10000, suffix: "+", label: "Shipments Handled" },
    { value: 99.9, suffix: "%", label: "Client Satisfaction" },
  ];

  return (
    <section className="py-24 md:py-32 bg-slate-900" data-testid="why-choose-us">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16 fade-up">
          <span className="text-blue-400 text-sm font-semibold tracking-wide uppercase mb-4 block">Why Choose Us</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            Why Choose Us
          </h2>
          <p className="mt-4 text-base text-slate-400 max-w-2xl mx-auto">
            Numbers That Speak For Themselves
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <Counter key={index} stat={stat} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Counter Component
const Counter = ({ stat, index }) => {
  const [count, setCount] = useState(0);
  const counterRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 2000;
          const start = Date.now();
          const end = stat.value;

          const animate = () => {
            const now = Date.now();
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            
            // Easing function
            const easeOut = 1 - Math.pow(1 - progress, 3);
            const current = Math.floor(easeOut * end);
            
            setCount(current);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(end);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [stat.value]);

  return (
    <div ref={counterRef} className="text-center fade-up" data-testid={`counter-${index}`}>
      <div className="text-4xl md:text-5xl font-bold text-white counter">
        {stat.value === 99.9 ? count.toFixed(1) : count.toLocaleString()}{stat.suffix}
      </div>
      <div className="text-slate-400 mt-2 text-sm font-medium">{stat.label}</div>
    </div>
  );
};

// Industries Section
const Industries = () => {
  const industries = [
    { icon: Factory, name: "Manufacturing" },
    { icon: ShoppingBag, name: "E-commerce" },
    { icon: Pill, name: "Pharmaceuticals" },
    { icon: Car, name: "Automotive" },
    { icon: Cpu, name: "Electronics" },
    { icon: FlaskConical, name: "Chemicals" },
    { icon: Shirt, name: "Textiles" },
    { icon: Package, name: "FMCG" },
    { icon: Globe, name: "+100 Industries" },
  ];

  return (
    <section id="industries" className="py-24 md:py-32 bg-white" data-testid="industries-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16 fade-up">
          <span className="section-subtitle">Industries We Serve</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
            Industries
          </h2>
          <p className="mt-4 text-base text-slate-500 max-w-2xl mx-auto">
            Expertise Across Sectors
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6">
          {industries.map((industry, index) => (
            <div key={index} className="industry-card fade-up" data-testid={`industry-${index}`}>
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <industry.icon className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="font-medium text-slate-900">{industry.name}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Locations Section
const Locations = () => {
  const regions = [
    { icon: MapPin, name: "Delhi NCR" },
    { icon: MapPin, name: "Haryana" },
    { icon: MapPin, name: "Uttar Pradesh" },
  ];

  const facilities = [
    { icon: Plane, name: "All Major Airports" },
    { icon: Ship, name: "All Seaports" },
    { icon: Building2, name: "ICD (Inland Container Depots)" },
    { icon: Package, name: "CFS (Container Freight Station)" },
  ];

  return (
    <section id="locations" className="py-24 md:py-32 bg-slate-50" data-testid="locations-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16 fade-up">
          <span className="section-subtitle">Our Coverage</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
            Locations
          </h2>
          <p className="mt-4 text-base text-slate-500">
            Comprehensive coverage across Northern India
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="location-card fade-up" data-testid="regions-covered">
            <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Regions Covered
            </h3>
            <div className="space-y-4">
              {regions.map((region, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-100">
                  <region.icon className="w-5 h-5 text-slate-500" />
                  <span className="font-medium text-slate-700">{region.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="location-card fade-up" data-testid="clearance-facilities">
            <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-green-600" />
              Clearance Facilities
            </h3>
            <div className="space-y-4">
              {facilities.map((facility, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-100">
                  <facility.icon className="w-5 h-5 text-slate-500" />
                  <span className="font-medium text-slate-700">{facility.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const testimonials = [
    { name: "Rajesh Kumar", company: "Kumar Electronics", text: "A.P Enterprises has been handling our imports for over 5 years. Their expertise and reliability are unmatched in the industry." },
    { name: "Priya Sharma", company: "Sharma Textiles", text: "Quick clearance times and excellent communication. They make customs clearance stress-free for our business." },
    { name: "Amit Patel", company: "Patel Pharmaceuticals", text: "Their knowledge of pharma regulations is exceptional. Highly recommended for pharmaceutical imports." },
    { name: "Sunita Gupta", company: "Gupta Automotive", text: "Professional team that understands urgent shipments. They've never let us down with deadlines." },
    { name: "Vikram Singh", company: "Singh Manufacturing", text: "Best customs clearing agent in Delhi NCR. Their 20+ years of experience really shows in their work." },
    { name: "Neha Jain", company: "Jain FMCG", text: "Cost-effective and efficient services. A.P Enterprises is our go-to partner for all customs needs." },
  ];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16 fade-up">
          <span className="section-subtitle">Testimonials</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
            Testimonials
          </h2>
          <p className="mt-4 text-base text-slate-500 max-w-2xl mx-auto">
            What Our Clients Say
          </p>
        </div>

        <div className="relative max-w-3xl mx-auto">
          <div className="testimonial-card text-center fade-up" data-testid="testimonial-card">
            <div className="mb-6">
              <Star className="w-6 h-6 text-yellow-400 inline-block" />
              <Star className="w-6 h-6 text-yellow-400 inline-block" />
              <Star className="w-6 h-6 text-yellow-400 inline-block" />
              <Star className="w-6 h-6 text-yellow-400 inline-block" />
              <Star className="w-6 h-6 text-yellow-400 inline-block" />
            </div>
            <p className="text-lg text-slate-600 mb-6 italic">"{testimonials[currentIndex].text}"</p>
            <div>
              <p className="font-semibold text-slate-900">{testimonials[currentIndex].name}</p>
              <p className="text-sm text-slate-500">{testimonials[currentIndex].company}</p>
            </div>
          </div>

          <div className="flex justify-center gap-4 mt-8">
            <button onClick={prevSlide} className="p-3 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" data-testid="testimonial-prev">
              <ChevronUp className="w-5 h-5 rotate-[-90deg]" />
            </button>
            <div className="flex gap-2 items-center">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${index === currentIndex ? 'bg-slate-900' : 'bg-slate-300'}`}
                  data-testid={`testimonial-dot-${index}`}
                />
              ))}
            </div>
            <button onClick={nextSlide} className="p-3 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" data-testid="testimonial-next">
              <ChevronDown className="w-5 h-5 rotate-[-90deg]" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Blog Section
const Blog = () => {
  const posts = [
    { 
      title: "Understanding Customs Clearance Process in India", 
      excerpt: "A comprehensive guide to navigating the customs clearance process for importers and exporters.",
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&q=80",
      date: "Jan 15, 2026"
    },
    { 
      title: "New GST Regulations for Import/Export in 2026", 
      excerpt: "Everything you need to know about the latest GST changes affecting international trade.",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80",
      date: "Jan 10, 2026"
    },
    { 
      title: "Top 5 Documents Required for Customs Clearance", 
      excerpt: "Essential documentation checklist for smooth customs clearance at Indian ports.",
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&q=80",
      date: "Jan 5, 2026"
    },
    { 
      title: "Benefits of Hiring a CHA Licensed Agent", 
      excerpt: "Why working with a licensed customs house agent can save you time and money.",
      image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&q=80",
      date: "Dec 28, 2025"
    },
  ];

  return (
    <section id="blog" className="py-24 md:py-32 bg-slate-50" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16 fade-up">
          <span className="section-subtitle">Our Blog</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
            Blog
          </h2>
          <p className="mt-4 text-base text-slate-500 max-w-2xl mx-auto">
            Latest Insights & Updates
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {posts.map((post, index) => (
            <div key={index} className="blog-card fade-up" data-testid={`blog-post-${index}`}>
              <div className="overflow-hidden">
                <img src={post.image} alt={post.title} className="blog-card-image" loading="lazy" />
              </div>
              <div className="p-5">
                <span className="text-xs text-slate-500 font-medium">{post.date}</span>
                <h3 className="text-base font-semibold text-slate-900 mt-2 mb-2 line-clamp-2">{post.title}</h3>
                <p className="text-sm text-slate-600 line-clamp-2">{post.excerpt}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQ = () => {
  const faqs = [
    { q: "What does a customs clearing agent do?", a: "A customs clearing agent facilitates the smooth passage of goods through customs by handling all documentation, duty payments, and regulatory compliance on behalf of importers and exporters." },
    { q: "Are you CHA licensed?", a: "Yes, A.P Enterprises is a fully licensed Customs House Agent (CHA) since 2011, authorized to operate across all major ports and airports in India." },
    { q: "What documents are required for customs clearance?", a: "Common documents include Bill of Lading/Airway Bill, Commercial Invoice, Packing List, IEC Certificate, GST registration, and product-specific licenses or permits." },
    { q: "How long does customs clearance take?", a: "Clearance time varies based on shipment type and documentation. Typically, air cargo takes 1-3 days and sea cargo takes 3-7 days with complete documentation." },
    { q: "Do you handle both import and export clearance?", a: "Yes, we provide comprehensive customs clearance services for both imports and exports across all modes of transport." },
    { q: "What are your service areas?", a: "We primarily serve Delhi NCR, Haryana, and Uttar Pradesh, with clearance services at all major airports, seaports, ICDs, and CFS locations across India." },
    { q: "Can you help with IEC registration?", a: "Yes, we provide complete assistance with IEC (Import Export Code) registration, DGFT compliance, and GST-related procedures." },
    { q: "What industries do you serve?", a: "We serve diverse industries including manufacturing, pharmaceuticals, automotive, electronics, textiles, chemicals, FMCG, and e-commerce among many others." },
  ];

  return (
    <section id="faq" className="py-24 md:py-32 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16 fade-up">
          <span className="section-subtitle">FAQ</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
            Frequently Asked Questions
          </h2>
        </div>

        <Accordion type="single" collapsible className="space-y-4 fade-up" data-testid="faq-accordion">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`} className="bg-slate-50 rounded-xl px-6 border-none" data-testid={`faq-item-${index}`}>
              <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline py-5">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 pb-5">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const formRef = useRef();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = {
      name: formRef.current.user_name.value,
      email: formRef.current.user_email.value,
      phone: formRef.current.user_phone.value,
      message: formRef.current.message.value,
      form_type: 'contact'
    };

    try {
     const result = await fetch('/api/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!result.ok) {
        throw new Error('Network response was not ok');
      }
      toast.success('Message sent successfully! We will get back to you soon.');
      formRef.current.reset();
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-slate-50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="fade-up">
            <span className="section-subtitle">Get In Touch</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-4">
              Contact Us
            </h2>
            <p className="text-base text-slate-500 mb-8">
              Ready to simplify your customs clearance process? Get in touch with our team today.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Phone</h4>
                  <a href={`tel:+91${PHONE_NUMBER}`} className="text-slate-600 hover:text-blue-600">+91 {PHONE_NUMBER}</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Email</h4>
                  <a href={`mailto:${EMAIL}`} className="text-slate-600 hover:text-blue-600">{EMAIL}</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Service Coverage</h4>
                  <p className="text-slate-600">Delhi NCR, Haryana, Uttar Pradesh</p>
                </div>
              </div>

              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-whatsapp inline-flex" data-testid="contact-whatsapp">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Chat on WhatsApp
              </a>
            </div>
          </div>

          <div className="fade-up">
            <form ref={formRef} onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100" data-testid="contact-form">
              <div className="space-y-5">
                <div>
                  <label htmlFor="contact_name" className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                  <input type="text" id="contact_name" name="user_name" required className="form-input" placeholder="Your name" data-testid="contact-name-input" />
                </div>
                <div>
                  <label htmlFor="contact_email" className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input type="email" id="contact_email" name="user_email" required className="form-input" placeholder="your@email.com" data-testid="contact-email-input" />
                </div>
                <div>
                  <label htmlFor="contact_phone" className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <input type="tel" id="contact_phone" name="user_phone" className="form-input" placeholder="+91 XXXXX XXXXX" data-testid="contact-phone-input" />
                </div>
                <div>
                  <label htmlFor="contact_message" className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                  <textarea id="contact_message" name="message" required className="form-input form-textarea" placeholder="How can we help you?" data-testid="contact-message-input"></textarea>
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="contact-submit-btn">
                  {loading ? <span className="spinner"></span> : <Send className="w-4 h-4" />}
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Callback Form Section
const CallbackForm = () => {
  const formRef = useRef();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = {
      user_name: formRef.current.user_name.value,
      user_email: formRef.current.user_email.value,
      user_phone: formRef.current.user_phone.value,
      preferred_time: formRef.current.preferred_time.value,
      message: formRef.current.message.value || 'Callback request',
      form_type: 'callback'
    };

    try {
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        formData,
        { publicKey: EMAILJS_CONFIG.publicKey }
      );
      toast.success('Callback request received! We will contact you soon.');
      formRef.current.reset();
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="callback" className="py-24 md:py-32 bg-white" data-testid="callback-section">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12 fade-up">
          <span className="section-subtitle">Request Callback</span>
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">
            We'll Call You Back
          </h2>
          <p className="mt-4 text-slate-600">Fill in your details and our team will reach out to you.</p>
        </div>

        <form ref={formRef} onSubmit={handleSubmit} className="bg-slate-50 rounded-2xl p-8 fade-up" data-testid="callback-form">
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="callback_name" className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                <input type="text" id="callback_name" name="user_name" required className="form-input" placeholder="Your name" data-testid="callback-name-input" />
              </div>
              <div>
                <label htmlFor="callback_phone" className="block text-sm font-medium text-slate-700 mb-2">Phone *</label>
                <input type="tel" id="callback_phone" name="user_phone" required className="form-input" placeholder="+91 XXXXX XXXXX" data-testid="callback-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="callback_email" className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input type="email" id="callback_email" name="user_email" className="form-input" placeholder="your@email.com" data-testid="callback-email-input" />
              </div>
              <div>
                <label htmlFor="callback_time" className="block text-sm font-medium text-slate-700 mb-2">Preferred Time</label>
                <select id="callback_time" name="preferred_time" className="form-input" data-testid="callback-time-select">
                  <option value="morning">Morning (9am - 12pm)</option>
                  <option value="afternoon">Afternoon (12pm - 5pm)</option>
                  <option value="evening">Evening (5pm - 8pm)</option>
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="callback_message" className="block text-sm font-medium text-slate-700 mb-2">Message (Optional)</label>
              <textarea id="callback_message" name="message" className="form-input form-textarea" placeholder="Brief description of your requirements" data-testid="callback-message-input"></textarea>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="callback-submit-btn">
              {loading ? <span className="spinner"></span> : <Phone className="w-4 h-4" />}
              {loading ? 'Submitting...' : 'Request Callback'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};



const FeedbackForm = () => {
  const formRef = useRef();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = {
      name: formRef.current.name.value,
      email: formRef.current.email.value,
      phone: formRef.current.phone.value,
      message: formRef.current.message.value,
      form_type: "feedback",
    };

    try {
      const res = await fetch("/api/email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error();

      toast.success("message sent successfully");
      formRef.current.reset();
    } catch (err) {
      toast.error("failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="block text-sm mb-2">Name</label>
        <input name="name" className="form-input" />
      </div>

      <div>
        <label className="block text-sm mb-2">Email *</label>
        <input name="email" type="email" required className="form-input" />
      </div>

      <div>
        <label className="block text-sm mb-2">Phone</label>
        <input name="phone" className="form-input" />
      </div>

      <div>
        <label className="block text-sm mb-2">Message *</label>
        <textarea name="message" required className="form-input" />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-slate-900 text-white py-3 rounded-xl font-medium hover:bg-slate-800 transition"
      >
        {loading ? "submitting..." : "send message"}
      </button>
    </form>
  );
};

 function ContactSection() {
  return (
    <>
    <div className="text-center mb-16 fade-up">
          <span className="section-subtitle">contact</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
            Contact Us
          </h2>
        </div>

    <div className="grid md:grid-cols-2 gap-12 items-start max-w-6xl mx-auto py-16 px-4">

      
      
      {/* LEFT */}
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-semibold text-slate-900">
            Contact Us
          </h2>
          <p className="text-slate-500 mt-2">
            ready to simplify your process? get in touch with us today.
          </p>
        </div>

        <div className="space-y-4">

          <div className="flex items-start gap-4 hover:bg-slate-50 p-3 rounded-xl transition">
            <div className="bg-blue-100 text-blue-600 p-3 rounded-xl">📞</div>
            <div>
              <p className="text-sm text-slate-500">Phone</p>
              <p className="font-medium text-slate-800">
                +91 8577000069
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 hover:bg-slate-50 p-3 rounded-xl transition">
            <div className="bg-green-100 text-green-600 p-3 rounded-xl">📧</div>
            <div>
              <p className="text-sm text-slate-500">Email</p>
              <p className="font-medium text-slate-800">
                apenterprisesindia.cc@gmail.com
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 hover:bg-slate-50 p-3 rounded-xl transition">
            <div className="bg-purple-100 text-purple-600 p-3 rounded-xl">📍</div>
            <div>
              <p className="text-sm text-slate-500">Service Coverage</p>
              <p className="font-medium text-slate-800">
                Delhi NCR, Haryana, Uttar Pradesh
              </p>
            </div>
          </div>

        </div>

        <a
          href="https://wa.me/918577000069"
          target="_blank"
          className="inline-block bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-full font-medium transition"
        >
          chat on whatsapp
        </a>
      </div>

      {/* RIGHT */}
      <div className="bg-white rounded-2xl p-8 shadow-md border border-slate-200 hover:shadow-lg transition">
        <FeedbackForm />
      </div>
    </div>
    </>
  );
}

// Social Media Section
const SocialMedia = () => {
  return (
    <section className="py-16 bg-slate-100" data-testid="social-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 text-center fade-up">
        <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-2">Connect With Us</h3>
        <p className="text-base text-slate-500 mb-8">Follow A.P Enterprises for customs updates, logistics insights, and industry information.</p>
        
        <div className="flex justify-center gap-4">
          <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="social-instagram">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="social-facebook">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>
          <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="social-linkedin">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
          <a href={SOCIAL_LINKS.twitter} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="social-twitter">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'About Us', href: '#about' },
    { name: 'Contact Us', href: '#contact' },
  ];

  const services = [
    { name: 'Customs Clearance', href: '#services' },
    { name: 'Documentation', href: '#services' },
    { name: 'Freight Forwarding', href: '#services' },
    { name: 'Logistics', href: '#services' },
  ];

  return (
    <footer className="bg-slate-900 text-white pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2 mb-12">
          {/* Branding */}
          <div className="lg:col-span-2">
            <img src={LOGO_URL} alt="A.P Enterprises" className="h-16 w-auto mb-4" />
            <p className="text-slate-400 text-sm leading-relaxed max-w-sm">
              CHA Licensed Customs Clearing Agent with 20+ Years of Trusted Service in Import/Export Clearance
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a href={link.href} className="footer-link">{link.name}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-white mb-4">Services</h4>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li key={index}>
                  <a href={service.href} className="footer-link">{service.name}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-slate-400 text-sm">
                <Phone className="w-4 h-4" />
                <a href={`tel:+91${PHONE_NUMBER}`} className="hover:text-white transition-colors">+91 {PHONE_NUMBER}</a>
              </li>
              <li className="flex items-start gap-2 text-slate-400 text-sm">
                <Mail className="w-4 h-4" />
              {  <a href={`mailto:${EMAIL}`} className="absolute ml-6 mb-1 hover:text-white transition-colors">{EMAIL}</a>}
              </li>
              <li className="flex items-start gap-2 text-slate-400 text-sm">
                <MapPin className="w-4 h-4 mt-0.5" />
                <span>Delhi NCR, Haryana, UP</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex gap-4">
            <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a href={SOCIAL_LINKS.twitter} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
             <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
           <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" x-file-name="App" x-line-number="1058" x-column="12" x-component="svg" x-id="App_1058_12" x-dynamic="false"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" x-file-name="App" x-line-number="1059" x-column="14" x-component="path" x-id="App_1059_14" x-dynamic="false"></path></svg>
            </a>
          </div>
          <p className="text-slate-500 text-sm">
            © 2026 A.P Enterprises. All rights Reserved. CHA Licensed Since 2011
          </p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const FloatingWhatsApp = () => {
  return (
    <a
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float animate-pulse-slow"
      data-testid="floating-whatsapp"
    >
      <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

// Main App Component
function App() {
  useEffect(() => {
    // Initialize GSAP ScrollTrigger animations
    const elements = document.querySelectorAll('.fade-up');
    
    elements.forEach((element) => {
      gsap.fromTo(element,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <div className="App" data-testid="app-container">
      <Toaster position="top-right" richColors />
      <Navbar />
      <main>
        <Hero />
        <TrustBar />
        <Services />
        <Process />
        <About />
        <WhyChooseUs />
        <Industries />
        <Locations />
        <Testimonials />
        <Blog />
        <FAQ />
       {/*  <Contact /> */}
       {/*  <FeedbackForm /> */}
       <ContactSection></ContactSection>
        <SocialMedia />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
